import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CssBaseline,
  ThemeProvider,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import theme from './theme/theme';
import AppRoutes from './routes/AppRoutes';
import SessionValidation from './components/SessionValidation';
import RendererLockedScreen from './components/organisms/RendererLockedScreen/RendererLockedScreen';
import SessionErrorScreen from './components/organisms/SessionErrorScreen/SessionErrorScreen';
import { SessionProvider, useSession } from './contexts/SessionContext';
import { initializeSession, shouldShowValidation, getCurrentSession, markValidationShown } from './utils/sessionManager';
import sessionService from './services/session.service';
import { fetchStatsConfig } from './services/statsConfig.service';
import { buildOrgPath } from './utils/routePaths';

// Inner component that consumes the session context
const AppContent = () => {
  const {
    isLoading,
    loadingMessage,
    showValidation,
    session,
    completeValidation,
    connectionError,
    retryConnection,
  } = useSession();

  // Fire-and-forget — warms the in-memory stats-config cache for the
  // session; TXLive falls back to the static list until this resolves (or
  // forever, if it fails).
  useEffect(() => {
    fetchStatsConfig();
  }, []);

  // OPUS-1106: a network failure while resolving the short URL/handoff —
  // show a recoverable error instead of spinning forever or falling through
  // to the generic "No session found" screen.
  if (connectionError) {
    return (
      <SessionErrorScreen
        data={{ message: connectionError }}
        retry={retryConnection}
      />
    );
  }

  // Loading State
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          gap: 2,
          backgroundColor: '#1E1E1E',
          color: '#FFFFFF'
        }}
      >
        <CircularProgress sx={{ color: '#45FFD5' }} />
        <Typography variant="body1" sx={{ color: '#FFFFFF' }}>
          {loadingMessage}
        </Typography>
      </Box>
    );
  }

  // Validation State
  if (showValidation) {
    return (
      <SessionValidation
        session={session}
        onValidationComplete={completeValidation}
      />
    );
  }

  // Main App State
  return <AppRoutes />;
};

function App() {
  const navigate = useNavigate();
  const [showValidation, setShowValidation] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [sessionInitialized, setSessionInitialized] = useState(false);
  const [sessionMessage, setSessionMessage] = useState("");
  const [isCheckingSession, setIsCheckingSession] = useState(false);
  const [lockedData, setLockedData] = useState(null);
  const [sessionErrorData, setSessionErrorData] = useState(null);

  useEffect(() => {
    const initializeApp = async () => {
      const currentUrl = window.location.href;

      // Renderer Service unreachable (fail-closed) — Session Service redirected
      // here with ?locked=. Locked and deleted renderers are both soft now (the
      // session still resolves via ?session=/?h=, with rendererLocked/
      // rendererDeleted flags embedded) — this channel is only used when the
      // renderer's live state genuinely could not be verified.
      const params = new URLSearchParams(window.location.search);
      const lockedParam = params.get('locked');
      if (lockedParam) {
        try {
          const data = JSON.parse(decodeURIComponent(atob(lockedParam)));
          params.delete('locked');
          const cleanUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
          window.history.replaceState({}, '', cleanUrl);
          // Persisted so a refresh re-shows the same lock screen instead of
          // falling through to "No session found" (the ?locked= param only
          // ever arrives once, on the original short-URL redirect). Scoped to
          // the path it was shown on — see the restore check below for why.
          sessionStorage.setItem('aedrive_locked_data', JSON.stringify({ ...data, __path: window.location.pathname }));
          setLockedData(data);
          setIsValidating(false);
          setSessionInitialized(true);
          return;
        } catch (_) {
          // Malformed param — fall through to normal init
        }
      }

      // Generic session error (not-found/expired/invalid/not-assigned) —
      // Session Service redirected here with ?sessionError=. Distinct from
      // ?locked= which is reserved for renderer-lock errors only.
      const sessionErrorParam = params.get('sessionError');
      if (sessionErrorParam) {
        try {
          const data = JSON.parse(decodeURIComponent(atob(sessionErrorParam)));
          params.delete('sessionError');
          const cleanUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
          window.history.replaceState({}, '', cleanUrl);
          // Scoped to the path it was shown on — see the restore check below.
          sessionStorage.setItem('aedrive_session_error_data', JSON.stringify({ ...data, __path: window.location.pathname }));
          setSessionErrorData(data);
          setIsValidating(false);
          setSessionInitialized(true);
          return;
        } catch (_) {
          // Malformed param — fall through to normal init
        }
      }

      // A fresh ?session= or ?h= arrival means a new short-URL resolution
      // succeeded (renderer is unlocked now) — any previously stored lock
      // or session-error screen is stale and must not resurface.
      if (currentUrl.includes('session=') || params.has('h')) {
        sessionStorage.removeItem('aedrive_locked_data');
        sessionStorage.removeItem('aedrive_session_error_data');
      } else {
        // No fresh ?locked=/?sessionError= param this load — check whether
        // we're refreshing a previously-shown lock or session-error screen.
        // Only restore if the stored screen was shown on THIS SAME PATH —
        // otherwise a stale entry from an earlier, unrelated visit (e.g. a
        // renderer that was deleted/unavailable at the time but is fine now,
        // or navigating straight to a different renderer's URL) would wrongly
        // resurface here and pre-empt AppRoutes' own live re-check, which
        // never gets a chance to run because this branch returns first.
        const currentPath = window.location.pathname;
        const storedLockedData = sessionStorage.getItem('aedrive_locked_data');
        if (storedLockedData) {
          try {
            const parsed = JSON.parse(storedLockedData);
            if (parsed.__path === currentPath) {
              setLockedData(parsed);
              setIsValidating(false);
              setSessionInitialized(true);
              return;
            }
            sessionStorage.removeItem('aedrive_locked_data');
          } catch (_) {
            sessionStorage.removeItem('aedrive_locked_data');
          }
        }

        const storedSessionErrorData = sessionStorage.getItem('aedrive_session_error_data');
        if (storedSessionErrorData) {
          try {
            const parsed = JSON.parse(storedSessionErrorData);
            if (parsed.__path === currentPath) {
              setSessionErrorData(parsed);
              setIsValidating(false);
              setSessionInitialized(true);
              return;
            }
            sessionStorage.removeItem('aedrive_session_error_data');
          } catch (_) {
            sessionStorage.removeItem('aedrive_session_error_data');
          }
        }
      }

      const hasSessionParam = currentUrl.includes("session=");

      const session = initializeSession(currentUrl);

      if (session && session.orgId) {
        const verifySession = localStorage.getItem("aedrive_session_data");
        if (!verifySession) {
          try {
            localStorage.setItem(
              "aedrive_session_data",
              JSON.stringify(session)
            );
            const verifyAgain = localStorage.getItem("aedrive_session_data");
            if (!verifyAgain) {
              setIsValidating(false);
              setSessionInitialized(true);
              return;
            }
          } catch (error) {
            setIsValidating(false);
            setSessionInitialized(true);
            return;
          }
        }

        // Don't create session yet - wait for validation and user choice

        if (shouldShowValidation(session)) {
          setShowValidation(true);
          setIsValidating(true);
          setSessionInitialized(true);
        } else {
          setIsValidating(false);
          setSessionInitialized(true);

          if (hasSessionParam) {
            setTimeout(() => {
              try {
                const urlObj = new URL(currentUrl);
                const currentPath = urlObj.pathname;

                if (currentPath === "/" || currentPath === "") {
                  const cleanPath = buildOrgPath(session.orgId);
                  window.history.replaceState({}, "", cleanPath);
                  navigate(cleanPath, { replace: true });
                } else {
                  urlObj.searchParams.delete("session");
                  const cleanUrl = urlObj.pathname + (urlObj.search || "");
                  window.history.replaceState({}, "", cleanUrl);
                }
              } catch (error) {
                const cleanPath = buildOrgPath(session.orgId);
                window.history.replaceState({}, "", cleanPath);
                navigate(cleanPath, { replace: true });
              }
            }, 100);
          }
        }
      } else {
        if (hasSessionParam) {
          setTimeout(() => {
            const retrySession = initializeSession(window.location.href);
            if (retrySession && retrySession.orgId) {
              setIsValidating(false);
              setSessionInitialized(true);
              const cleanPath = buildOrgPath(retrySession.orgId);
              window.history.replaceState({}, "", cleanPath);
              navigate(cleanPath, { replace: true });
            } else {
              setIsValidating(false);
              setSessionInitialized(true);
            }
          }, 500);
        } else {
          setIsValidating(false);
          setSessionInitialized(true);
        }
      }
    };

    initializeApp();
  }, [navigate]);

  const handleValidationComplete = async () => {
    const session = getCurrentSession();
    if (session?.orgId) {
      markValidationShown(session);
      setShowValidation(false);
      setIsValidating(false);

      setIsCheckingSession(true);
      setSessionMessage("Initializing new session...");

      try {
        await sessionService.createOrUpdateSession();
      } catch (error) {
        console.error("Error creating new session:", error);
      }

      setIsCheckingSession(false);

      const cleanPath = buildOrgPath(session.orgId);
      window.history.replaceState({}, "", cleanPath);
      navigate(cleanPath, { replace: true });
    } else {
      setIsValidating(false);
      setIsCheckingSession(false);
    }
  };

  if (lockedData) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <RendererLockedScreen data={lockedData} />
      </ThemeProvider>
    );
  }

  if (sessionErrorData) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SessionErrorScreen data={sessionErrorData} />
      </ThemeProvider>
    );
  }

  if (!sessionInitialized || isCheckingSession) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            gap: 2,
            backgroundColor: "#1E1E1E",
            color: "#FFFFFF",
          }}
        >
          <CircularProgress sx={{ color: "#45FFD5" }} />
          <Typography variant="body1" sx={{ color: "#FFFFFF" }}>
            {sessionMessage || "Initializing..."}
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  if (isValidating && showValidation) {
    const session = getCurrentSession();
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SessionValidation
          session={session}
          onValidationComplete={handleValidationComplete}
        />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SessionProvider>
        <AppContent />
      </SessionProvider>
    </ThemeProvider>
  );
}

export default App;
